import React, { useState, useEffect } from 'react';

import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Mail, 
  Phone, 
  MapPin, 
  MessageCircle,
  Facebook,
  Linkedin,
  Twitter,
  Shield,
  Award,
  Clock,
  FileText,
  Settings
} from 'lucide-react';
import { companyInfo } from '../mock';
import { CGVPage } from './CGVPage';

export const Footer = () => {
  const [showCGV, setShowCGV] = useState(false);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    const handler = (e) => setPending((prev) => (typeof e.detail === 'number' ? e.detail : prev));
    window.addEventListener('comments-pending-count', handler);
    return () => window.removeEventListener('comments-pending-count', handler);
  }, []);

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Accueil', href: '#home' },
    { name: 'Produits', href: '#products' },
    { name: 'Services', href: '#services' },
    { name: 'Réalisations', href: '#installations' },
    { name: 'Contact', href: '#contact' }
  ];

  const productCategories = [
    'Batteries Lithium PowMr',
    'Convertisseurs Hybrides',
    'Kits Solaires Complets',
    'Accessoires & Composants'
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (showCGV) {
    return <CGVPage onBack={() => setShowCGV(false)} />;
  }

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/logos/zn-logo.png" 
                alt="Z&N Solar Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 className="text-xl font-bold">Z&N Solar</h3>
                <p className="text-sm text-gray-400">L'excellence sans compromis</p>
              </div>
            </div>
            
            <p className="text-gray-300 text-sm leading-relaxed">
              <strong>Distributeur et Représentant Officiel EXCLUSIF PowMr au Mali.</strong><br/>
              Spécialiste en solutions énergétiques haute performance. 
              Batteries lithium et convertisseurs conformes aux normes européennes.
            </p>

            <div className="flex space-x-2">
              <Badge className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-black hover:from-yellow-700 hover:to-yellow-800 border-0">
                <Shield className="w-3 h-3 mr-1" />
                CE
              </Badge>
              <Badge className="bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800 border-0">
                <Award className="w-3 h-3 mr-1" />
                ISO 9001
              </Badge>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-300 hover:text-yellow-400 hover:translate-x-1 transition-all duration-200 text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href="/?admin=1"
                  className="relative text-gray-300 hover:text-yellow-400 hover:translate-x-1 transition-all duration-200 text-sm flex items-center space-x-1"
                >
                  <Settings className="w-3 h-3" />
                  <span>Administration</span>
                  {pending > 0 && (
                    <span className="absolute -top-1 -right-2 inline-flex items-center justify-center w-3.5 h-3.5 text-[10px] bg-red-600 text-white rounded-full">{pending}</span>
                  )}
                </a>
              </li>
              <li>
                <button
                  onClick={() => setShowCGV(true)}
                  className="text-gray-300 hover:text-yellow-400 hover:translate-x-1 transition-all duration-200 text-sm flex items-center space-x-1"
                >
                  <FileText className="w-3 h-3" />
                  <span>CGV</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Nos Produits</h4>
            <ul className="space-y-3">
              {productCategories.map((product) => (
                <li key={product}>
                  <span className="text-gray-300 text-sm">
                    {product}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact</h4>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">{companyInfo.contact.phone}</span>
              </div>
              
              <div className="flex items-start space-x-3">
                <Mail className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  {companyInfo.contact.emails.map((email, index) => (
                    <div key={index} className="text-gray-300 text-sm">{email}</div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {companyInfo.addresses.map((address, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <MapPin className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-gray-300 text-sm font-medium">{address.type}</p>
                      <p className="text-gray-400 text-xs">{address.address}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '33623737780'}?text=Bonjour Z&N Solar, je souhaite obtenir des informations sur vos solutions énergétiques PowMr`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 text-sm"
              >
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>

        <Separator className="bg-gray-700 mb-8" />

        {/* Services & Guarantees */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div className="text-center">
            <Shield className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
            <h5 className="font-semibold mb-2">Garantie 10 ans</h5>
            <p className="text-gray-400 text-sm">Sur toutes nos batteries lithium PowMr</p>
          </div>
          <div className="text-center">
            <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
            <h5 className="font-semibold mb-2">Support 24h</h5>
            <p className="text-gray-400 text-sm">Réponse garantie sous 24h</p>
          </div>
          <div className="text-center">
            <Award className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
            <h5 className="font-semibold mb-2">Conformité CE</h5>
            <p className="text-gray-400 text-sm">Normes européennes respectées</p>
          </div>
        </div>

        <Separator className="bg-gray-700 mb-8" />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-center md:text-left">
            <p className="text-gray-400 text-sm">
              © {currentYear} Z&N Solar. Tous droits réservés.
            </p>
            <p className="text-gray-500 text-xs mt-1">
              Zeïna-Nina SARL | Siège : Bamako, Mali | Bureau : Moisenay, France
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">Suivez-nous :</span>
            <div className="flex space-x-3">
              <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Float Button */}
      <a
        href={`https://wa.me/33623737780?text=Bonjour Z&N Solar, je souhaite obtenir des informations sur vos solutions énergétiques PowMr`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-40 group"
        aria-label="Contacter via WhatsApp"
      >
        <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
      </a>
    </footer>
  );
};